import { makeStyles } from '@material-ui/core';
import { motion } from 'framer-motion';
import React from 'react';
import { X } from 'react-feather';
import { IconButton } from '.';
import { theme } from '../../theme/theme';

const useDrawerClasses = makeStyles({
    root: {
        position: 'fixed',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: 10,
    },
    content: {
        position: 'fixed',
        top: 66,
        bottom: 0,
        display: 'flex',
        width: 850,
        borderRadius: '24px 0 0 0',
        boxShadow: '0 0 30px 30px rgba(0, 0, 0, 0.15)',
        background: theme.colors.white,
    },
    icon: {
        position: 'absolute',
        right: 15,
        top: 15,
        color: theme.colors.darkBlue[5],
    },
});

/**
 * Display a drawer on the right side of the screen.
 *
 * @param props.open set the drawer state to opened or closed
 * @param props.onClose callback triggered when the close button is clicked, the close button is only rendered if the
 *        callback is provided
 * @param props.onAfterClose callback triggered when the drawer finishes closing
 * @param props.children enforced type
 */
export const Drawer = (props: {
    open: boolean;
    onClose: () => void;
    onAfterClose?: () => void;
    children: React.ReactNode;
}) => {
    const classes = useDrawerClasses();
    const overflow = React.useRef('');
    const rootVariants = {
        open: { backgroundColor: 'rgba(0, 0, 0, 0.25)', display: 'flex' },
        close: { backgroundColor: 'rgba(0, 0, 0, 0)', transitionEnd: { display: 'none' } },
    };
    const contentVariants = { open: { right: 0 }, close: { right: -850 } };

    React.useLayoutEffect(() => {
        const rootOverflow = window.getComputedStyle(document.documentElement).overflow;
        overflow.current = rootOverflow != 'hidden' ? rootOverflow : overflow.current;
        document.documentElement.style.overflow = props.open ? 'hidden' : overflow.current;
    }, [props.open]);

    return (
        <motion.div
            className={classes.root}
            variants={rootVariants}
            initial='close'
            animate={props.open ? 'open' : 'close'}
            transition={{ duration: theme.transitions.default }}
            onClick={event => event.target === event.currentTarget && props.onClose?.()}
            onAnimationComplete={() => !props.open && props.onAfterClose?.()}
        >
            <motion.div
                className={classes.content}
                variants={contentVariants}
                initial='close'
                animate={props.open ? 'open' : 'close'}
                transition={{ duration: theme.transitions.default }}
            >
                {props.onClose && (
                    <IconButton className={classes.icon} onClick={props.onClose}>
                        <X />
                    </IconButton>
                )}
                {props.children}
            </motion.div>
        </motion.div>
    );
};
